import { useState } from "react";
import { ToolContainer } from "@/components/ToolLayout";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Printer } from "lucide-react";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { useLogUsageOnAction } from "@/hooks/useLogUsageOnAction";
import { NoticeBox } from "@/components/NoticeBox";

const TOOL_INFO_CONTENT = {
  what: `An invention disclosure worksheet for organizing and dating the details of an invention before filing anything or talking to a patent attorney — not a patent application itself.`,
  how: `Fill in the invention's title, background, detailed description, and novel features, and export the completed worksheet as a PDF with signature lines for you and a witness.`,
  tip: `Actual patent claims — the legal language defining what's protected — need to be drafted by a registered patent attorney; use this worksheet to organize your thinking and establish a dated record before that conversation, not as a substitute for it.`,
};

export default function InventionDisclosureWorksheet() {
  const [title, setTitle] = useState("");
  const [inventors, setInventors] = useState("");
  const [conceptionDate, setConceptionDate] = useState("");
  const [field, setField] = useState("");
  const [background, setBackground] = useState("");
  const [description, setDescription] = useState("");
  const [novelFeatures, setNovelFeatures] = useState("");
  const [alternatives, setAlternatives] = useState("");
  const [priorArt, setPriorArt] = useState("");

  const { logUsageNow } = useLogUsageOnAction();


  return (
    <ToolContainer>
      <style>{`
        @media print {
          /* display:none (not visibility:hidden) so the hidden page does not add blank pages */
          body *:not(#disclosure-print-area):not(#disclosure-print-area *):not(:has(#disclosure-print-area)) { display: none !important; }
          html, body { height: auto !important; overflow: visible !important; }
          #disclosure-print-area { position: static; width: 100%; }
          @page { margin: 1in; }
        }
      `}</style>

      <NoticeBox className="p-4 mb-6 print:hidden">
        <div className="flex gap-2">
          <AlertTriangle className="h-4 w-4 text-primary shrink-0 mt-0.5" />
          <p className="text-sm">
            This is an <strong>invention disclosure worksheet</strong>, not a
            patent application. It helps you organize and date your invention
            details before talking to a registered patent attorney or filing
            anything with a patent office. Actual patent claims — the legal
            language that defines what's protected — need to be drafted by a
            patent attorney or agent; getting them wrong can genuinely cost you
            your protection. This worksheet does not file anything and does not
            establish any legal rights on its own.
          </p>
        </div>
        <div className="flex gap-2 mt-3">
          <AlertTriangle className="h-4 w-4 text-primary shrink-0 mt-0.5" />
          <p className="text-sm">
            <strong>Do not publish or sell your invention before you get advice.</strong> Public disclosure
            (a post, talk, demo, crowdfunding page or offer for sale) can start a deadline or end your right to a
            patent in many countries. In the United States there is generally a one-year grace period; many other
            countries have none. Share this worksheet only under a signed NDA, or with your patent attorney.
          </p>
        </div>
      </NoticeBox>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="space-y-4 print:hidden">
          <div>
            <Label className="mb-1 block text-sm">Title of Invention</Label>
            <Input aria-label="Title of Invention" value={title} onChange={e => setTitle(e.target.value)} />
          </div>
          <div>
            <Label className="mb-1 block text-sm">
              Inventor(s) — Full Name(s)
            </Label>
            <Input
              value={inventors}
              onChange={e => setInventors(e.target.value)}
            />
          </div>
          <div>
            <Label className="mb-1 block text-sm">Date of Conception</Label>
            <Input aria-label="Date of Conception"
              type="date"
              value={conceptionDate}
              onChange={e => setConceptionDate(e.target.value)}
            />
          </div>
          <div>
            <Label className="mb-1 block text-sm">
              Field of Invention / Technology Area
            </Label>
            <Input
              value={field}
              onChange={e => setField(e.target.value)}
              placeholder="e.g. wireless charging devices"
            />
          </div>
          <div>
            <Label className="mb-1 block text-sm">
              Background — What Problem Does This Solve?
            </Label>
            <Textarea
              value={background}
              onChange={e => setBackground(e.target.value)}
              className="min-h-[80px]"
            />
          </div>
          <div>
            <Label className="mb-1 block text-sm">
              Detailed Description of the Invention
            </Label>
            <Textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              className="min-h-[120px]"
            />
          </div>
          <div>
            <Label className="mb-1 block text-sm">
              Key Novel Features (what makes this new/different)
            </Label>
            <Textarea
              value={novelFeatures}
              onChange={e => setNovelFeatures(e.target.value)}
              className="min-h-[80px]"
            />
          </div>
          <div>
            <Label className="mb-1 block text-sm">
              Alternative Versions or Embodiments
            </Label>
            <Textarea
              value={alternatives}
              onChange={e => setAlternatives(e.target.value)}
              className="min-h-[60px]"
            />
          </div>
          <div>
            <Label className="mb-1 block text-sm">
              Known Prior Art (similar existing products/patents, if any)
            </Label>
            <Textarea
              value={priorArt}
              onChange={e => setPriorArt(e.target.value)}
              className="min-h-[60px]"
            />
          </div>
          <Button onClick={async () => { const allowed = await logUsageNow(); if (!allowed) return; window.print(); }}>
            <Printer className="h-4 w-4 mr-2" /> Print / Save as PDF
          </Button>
        </div>

        <div
          id="disclosure-print-area"
          className="rounded-lg border p-8 bg-card print:border-0 text-sm"
        >
          <div className="text-xl font-bold text-center mb-1">
            Invention Disclosure Worksheet
          </div>
          <p className="text-center text-xs text-muted-foreground mb-6">
            Not a patent application — for pre-filing organization only
          </p>

          <div className="space-y-4">
            <div>
              <p className="font-semibold">Title of Invention</p>
              <p>{title || "—"}</p>
            </div>
            <div>
              <p className="font-semibold">Inventor(s)</p>
              <p>{inventors || "—"}</p>
            </div>
            <div>
              <p className="font-semibold">Date of Conception</p>
              <p>{conceptionDate || "—"}</p>
            </div>
            <div>
              <p className="font-semibold">Field of Invention</p>
              <p>{field || "—"}</p>
            </div>
            <div>
              <p className="font-semibold">Background</p>
              <p className="whitespace-pre-wrap">{background || "—"}</p>
            </div>
            <div>
              <p className="font-semibold">Detailed Description</p>
              <p className="whitespace-pre-wrap">{description || "—"}</p>
            </div>
            <div>
              <p className="font-semibold">Key Novel Features</p>
              <p className="whitespace-pre-wrap">{novelFeatures || "—"}</p>
            </div>
            <div>
              <p className="font-semibold">Alternative Embodiments</p>
              <p className="whitespace-pre-wrap">{alternatives || "—"}</p>
            </div>
            <div>
              <p className="font-semibold">Known Prior Art</p>
              <p className="whitespace-pre-wrap">{priorArt || "—"}</p>
            </div>
          </div>

          <div className="mt-10 pt-6 border-t space-y-6">
            <div className="flex justify-between">
              <div className="w-5/12 border-t border-foreground pt-1 text-xs">
                Inventor Signature / Date
              </div>
              <div className="w-5/12 border-t border-foreground pt-1 text-xs">
                Witness Signature / Date
              </div>
            </div>
          </div>
        </div>
      </div>
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
